<template>
  <div class="card">
    <div class="card-body">
      <div class="pl-header">
        <div class="pl-view-toggle">
          <button :class="['view-btn', { active: viewMode === 'month' }]" @click="setViewMode('month')">
            <i class="fas fa-calendar-alt mr-1"></i>月別
          </button>
          <button :class="['view-btn', { active: viewMode === 'staff' }]" @click="setViewMode('staff')">
            <i class="fas fa-user mr-1"></i>人別 (全期間)
          </button>
        </div>
        <div v-if="viewMode === 'month'" class="month-selector">
          <div class="year-selector">
            <button class="year-nav-btn" @click="changeYear(-1)"><i class="fas fa-chevron-left"></i></button>
            <span class="year-label">{{ currentDate.getFullYear() }}年</span>
            <button class="year-nav-btn" @click="changeYear(1)"><i class="fas fa-chevron-right"></i></button>
          </div>
          <div class="month-btn-group">
            <button v-for="m in 12" :key="m" :class="['month-btn', { active: currentDate.getMonth() === m - 1 }]" @click="selectMonth(m - 1)">{{ m }}月</button>
          </div>
        </div>
        <div class="pl-toolbar">
          <div class="pl-filter">
            <label class="pl-filter-label">スタッフ:</label>
            <select v-model="filterStaffId" class="form-select form-select-sm" style="width:auto;">
              <option :value="null">全員</option>
              <option v-for="s in staffList" :key="s.id" :value="s.id">{{ s.name }}</option>
            </select>
          </div>
          <button class="btn btn-success btn-sm" @click="openCreateModal">
            <i class="fas fa-plus mr-1"></i>新規作成
          </button>
        </div>
      </div>

      <!-- Create modal -->
      <div v-if="showCreateModal" class="pl-modal-overlay" @click.self="showCreateModal = false">
        <div class="pl-modal-box">
          <div class="pl-modal-header">
            <h6 class="pl-modal-title"><i class="fas fa-plus mr-2"></i>有給を作成 (管理者)</h6>
            <button type="button" class="pl-modal-close" @click="showCreateModal = false">&times;</button>
          </div>
          <div class="pl-modal-body">
            <div class="pl-field">
              <label>スタッフ <span class="required">*</span></label>
              <select v-model="createForm.staff_id" class="form-select">
                <option v-for="s in staffList" :key="s.id" :value="s.id">{{ s.name }}</option>
              </select>
            </div>
            <div class="pl-field">
              <label>種別 <span class="required">*</span></label>
              <div class="pl-radio-group">
                <label><input type="radio" :value="1" v-model="createForm.leave_type"> 全日</label>
                <label><input type="radio" :value="2" v-model="createForm.leave_type"> 半日</label>
              </div>
            </div>
            <div class="pl-field-row">
              <div class="pl-field">
                <label>開始日 <span class="required">*</span></label>
                <input type="date" v-model="createForm.start_date" class="form-control"
                       @change="onStartDateChange">
              </div>
              <div class="pl-field" v-if="createForm.leave_type !== 2">
                <label>終了日 <span class="required">*</span></label>
                <input type="date" v-model="createForm.end_date" class="form-control"
                       :min="createForm.start_date">
              </div>
            </div>
            <div class="pl-field">
              <label>理由</label>
              <textarea v-model="createForm.reason" class="form-control" rows="2" placeholder="任意"></textarea>
            </div>
            <p class="pl-note">※ 管理者作成のため<strong>承認済み</strong>として登録されます</p>
          </div>
          <div class="pl-modal-footer">
            <button class="btn btn-outline-secondary btn-sm" @click="showCreateModal = false">キャンセル</button>
            <button class="btn btn-success btn-sm" :disabled="!canSubmitCreate" @click="submitCreate">
              <i class="fas fa-check mr-1"></i>作成
            </button>
          </div>
        </div>
      </div>

      <!-- 月別ビュー -->
      <div v-if="viewMode === 'month'" class="table-responsive">
        <table class="table table-hover pl-table">
          <thead>
            <tr>
              <th>スタッフ</th>
              <th>種別</th>
              <th>期間</th>
              <th>日数</th>
              <th>申請理由</th>
              <th>ステータス</th>
              <th style="width:180px;">操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="group in groupedLeaves" :key="group.key">
              <td class="td-name">{{ group.name }}</td>
              <td><span :class="group.leaveType === 2 ? 'badge-half' : 'badge-full'">{{ group.leaveTypeText }}</span></td>
              <td class="td-date">{{ group.dateRange }}</td>
              <td>{{ group.displayCount }}</td>
              <td class="td-reason">{{ group.reason || '-' }}</td>
              <td>
                <span :class="statusClass(group.statusText)">{{ group.statusText }}</span>
              </td>
              <td class="td-actions">
                <div class="action-btns">
                  <button v-if="group.pendingCount > 0" class="btn btn-sm btn-approve" @click="bulkApprove(group.ids, 2)" title="承認">
                    <i class="fas fa-check mr-1"></i>承認
                  </button>
                  <button v-if="group.pendingCount > 0" class="btn btn-sm btn-revision" @click="bulkApprove(group.ids, 3)" title="却下">
                    <i class="fas fa-times mr-1"></i>却下
                  </button>
                  <button class="btn btn-sm btn-outline-secondary" @click="exportGroupPdf(group)" title="PDF">
                    <i class="fas fa-file-pdf"></i>
                  </button>
                  <button v-if="group.allApproved" class="btn btn-sm btn-outline-danger" @click="bulkDelete(group.ids)" title="削除">
                    <i class="fas fa-trash"></i>
                  </button>
                </div>
              </td>
            </tr>
            <tr v-if="groupedLeaves.length === 0">
              <td colspan="7" class="text-center text-muted py-4">有給申請がありません</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- 人別ビュー (全期間) アコーディオン形式 -->
      <div v-if="viewMode === 'staff'">
        <div v-if="staffSummaries.length === 0" class="text-center text-muted py-4">有給申請がありません</div>
        <div class="staff-actions" v-if="staffSummaries.length > 0">
          <button class="btn btn-sm btn-outline-secondary" @click="expandAll">
            <i class="fas fa-expand-alt mr-1"></i>全て展開
          </button>
          <button class="btn btn-sm btn-outline-secondary" @click="collapseAll">
            <i class="fas fa-compress-alt mr-1"></i>全て畳む
          </button>
        </div>
        <div v-for="ss in staffSummaries" :key="ss.staffId" class="staff-section">
          <div class="staff-section-header staff-section-clickable" @click="toggleStaffExpanded(ss.staffId)">
            <span class="staff-section-name">
              <i :class="['fas', expandedStaff[ss.staffId] ? 'fa-chevron-down' : 'fa-chevron-right', 'mr-2']"></i>
              <i class="fas fa-user mr-2"></i>{{ ss.staffName }}
            </span>
            <span class="staff-section-summary">
              申請数 <strong>{{ ss.total }}件</strong>
              / 承認済み <strong style="color:#3182ce;">{{ ss.approvedDays }}日</strong>
              / 承認待ち <strong style="color:#ed8936;">{{ ss.pendingDays }}日</strong>
            </span>
          </div>
          <div v-if="expandedStaff[ss.staffId]" class="table-responsive">
            <table class="table table-hover pl-table">
              <thead>
                <tr>
                  <th>種別</th>
                  <th>期間</th>
                  <th>日数</th>
                  <th>申請理由</th>
                  <th>ステータス</th>
                  <th style="width:180px;">操作</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="group in ss.groups" :key="group.key">
                  <td><span :class="group.leaveType === 2 ? 'badge-half' : 'badge-full'">{{ group.leaveTypeText }}</span></td>
                  <td class="td-date">{{ group.dateRange }}</td>
                  <td>{{ group.displayCount }}</td>
                  <td class="td-reason">{{ group.reason || '-' }}</td>
                  <td><span :class="statusClass(group.statusText)">{{ group.statusText }}</span></td>
                  <td class="td-actions">
                    <div class="action-btns">
                      <button v-if="group.pendingCount > 0" class="btn btn-sm btn-approve" @click="bulkApprove(group.ids, 2)">
                        <i class="fas fa-check mr-1"></i>承認
                      </button>
                      <button v-if="group.pendingCount > 0" class="btn btn-sm btn-revision" @click="bulkApprove(group.ids, 3)">
                        <i class="fas fa-times mr-1"></i>却下
                      </button>
                      <button class="btn btn-sm btn-outline-secondary" @click="exportGroupPdf(group)" title="PDF">
                        <i class="fas fa-file-pdf"></i>
                      </button>
                      <button v-if="group.allApproved" class="btn btn-sm btn-outline-danger" @click="bulkDelete(group.ids)" title="削除">
                        <i class="fas fa-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
                <tr v-if="ss.groups.length === 0">
                  <td colspan="6" class="text-center text-muted py-4">該当データがありません</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    let viewMode = 'month';
    try {
      const saved = sessionStorage.getItem('paidLeavePage.viewMode');
      if (saved === 'month' || saved === 'staff') viewMode = saved;
    } catch (e) { /* ignore */ }
    return {
      currentDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
      leaves: [],
      staffList: [],
      filterStaffId: null,
      viewMode,
      expandedStaff: {}, // 人別ビューのアコーディオン開閉状態
      showCreateModal: false,
      createForm: {
        staff_id: null,
        leave_type: 1,
        start_date: this._today(),
        end_date: this._today(),
        reason: '',
      },
    };
  },
  computed: {
    canSubmitCreate() {
      const f = this.createForm;
      if (!f.staff_id || !f.start_date) return false;
      if (f.leave_type !== 2 && !f.end_date) return false;
      return true;
    },
    filteredLeaves() {
      if (!this.filterStaffId) return this.leaves;
      return this.leaves.filter(l => l.staff_id === this.filterStaffId);
    },
    groupedLeaves() {
      // request_group_id でグルーピング（申請単位）
      const groupMap = {};

      this.filteredLeaves.forEach(l => {
        // グループキー: request_group_id があればそれ、なければ staff+date で1件1グループ
        const gKey = l.request_group_id || (l.staff_name + '-' + l.date + '-' + l.id);
        if (!groupMap[gKey]) {
          groupMap[gKey] = {
            staffName: l.staff_name,
            reason: l.reason || '',
            ids: [l.id],
            leaves: [l],
            firstDate: new Date(l.date),
            lastDate: new Date(l.date),
            pendingCount: l.approval_status === 1 ? 1 : 0,
          };
        } else {
          groupMap[gKey].ids.push(l.id);
          groupMap[gKey].leaves.push(l);
          const lDate = new Date(l.date);
          if (lDate < groupMap[gKey].firstDate) groupMap[gKey].firstDate = lDate;
          if (lDate > groupMap[gKey].lastDate) groupMap[gKey].lastDate = lDate;
          if (l.approval_status === 1) groupMap[gKey].pendingCount++;
        }
      });

      return Object.values(groupMap).map(c => this._buildGroup(c))
        .sort((a, b) => a.name.localeCompare(b.name, 'ja') || a.dateRange.localeCompare(b.dateRange));
    },
    staffSummaries() {
      // 人別ビュー: スタッフごとに集計
      const map = {};
      this.filteredLeaves.forEach(l => {
        const sid = l.staff_id;
        if (!map[sid]) {
          map[sid] = {
            staffId: sid,
            staffName: l.staff_name,
            leaves: [],
            approvedDays: 0,
            pendingDays: 0,
          };
        }
        map[sid].leaves.push(l);
        const consumed = (l.leave_type === 2) ? 0.5 : 1;
        if (l.approval_status === 2) map[sid].approvedDays += consumed;
        if (l.approval_status === 1) map[sid].pendingDays += consumed;
      });

      // 各スタッフ内で request_group_id ごとにグループ化
      return Object.values(map).map(staff => {
        const groupMap = {};
        staff.leaves.forEach(l => {
          const gKey = l.request_group_id || (staff.staffId + '-' + l.date + '-' + l.id);
          if (!groupMap[gKey]) {
            groupMap[gKey] = {
              staffName: staff.staffName,
              reason: l.reason || '',
              ids: [l.id],
              leaves: [l],
              firstDate: new Date(l.date),
              lastDate: new Date(l.date),
              pendingCount: l.approval_status === 1 ? 1 : 0,
            };
          } else {
            groupMap[gKey].ids.push(l.id);
            groupMap[gKey].leaves.push(l);
            const lDate = new Date(l.date);
            if (lDate < groupMap[gKey].firstDate) groupMap[gKey].firstDate = lDate;
            if (lDate > groupMap[gKey].lastDate) groupMap[gKey].lastDate = lDate;
            if (l.approval_status === 1) groupMap[gKey].pendingCount++;
          }
        });
        const groups = Object.values(groupMap)
          .map(c => this._buildGroup(c))
          .sort((a, b) => b.dateRange.localeCompare(a.dateRange));
        return {
          staffId: staff.staffId,
          staffName: staff.staffName,
          total: staff.leaves.length,
          approvedDays: Math.round(staff.approvedDays * 10) / 10,
          pendingDays: Math.round(staff.pendingDays * 10) / 10,
          groups,
        };
      }).sort((a, b) => a.staffName.localeCompare(b.staffName, 'ja'));
    },
  },
  mounted() {
    this.loadLeaves();
    this.loadStaffList();
  },
  methods: {
    _today() {
      const d = new Date();
      return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
    },
    loadStaffList() {
      const shop_id = this.$store.getters.shop;
      this.$http.get(process.env.VUE_APP_API_PATH + '/shop/staffList/' + shop_id)
        .then(res => {
          const shop = res.data?.data?.[0] || {};
          this.staffList = (shop.staff || []).map(s => ({ id: s.id, name: s.name }));
        })
        .catch(() => { /* silent */ });
    },
    openCreateModal() {
      // フィルタが「全員」(null)のときは、リストの先頭スタッフを初期値にする
      const defaultStaffId = this.filterStaffId
        || (this.staffList.length > 0 ? this.staffList[0].id : null);
      this.createForm = {
        staff_id: defaultStaffId,
        leave_type: 1,
        start_date: this._today(),
        end_date: this._today(),
        reason: '',
      };
      this.showCreateModal = true;
    },
    onStartDateChange() {
      // 開始日 > 終了日になったら終了日を開始日にそろえる
      if (this.createForm.end_date < this.createForm.start_date) {
        this.createForm.end_date = this.createForm.start_date;
      }
    },
    async submitCreate() {
      if (!this.canSubmitCreate) return;
      const shop_id = this.$store.getters.shop;
      const payload = {
        staff_id: this.createForm.staff_id,
        leave_type: this.createForm.leave_type,
        start_date: this.createForm.start_date,
        end_date: this.createForm.leave_type === 2 ? this.createForm.start_date : this.createForm.end_date,
        reason: this.createForm.reason || null,
      };
      try {
        const res = await this.$http.post(
          process.env.VUE_APP_API_PATH + '/shop/paidLeave/admin-create/' + shop_id,
          payload
        );
        const created = res.data?.data?.created || 0;
        this.$toast.success(created + '件の有給を作成しました');
        this.showCreateModal = false;
        this.loadLeaves();
      } catch (e) {
        const msg = e.response?.data?.message || '作成に失敗しました';
        this.$toast.error(msg);
      }
    },
    _dayDiff(d1, d2) {
      return Math.round((d2 - d1) / (1000 * 60 * 60 * 24));
    },
    _formatDate(d) {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return y + '-' + m + '-' + day;
    },
    _buildGroup(c) {
      const count = c.ids.length;
      const startStr = this._formatDate(c.firstDate);
      const endStr = this._formatDate(c.lastDate);
      const dateRange = count > 1 ? startStr + ' 〜 ' + endStr : startStr;
      const allApproved = c.leaves.every(l => l.approval_status === 2);
      const allPending = c.leaves.every(l => l.approval_status === 1);
      const statusText = allApproved ? '承認済み' : allPending ? '承認待ち' : '混在';
      const leaveType = c.leaves[0]?.leave_type || 1;
      const leaveTypeText = leaveType === 2 ? '半日有給' : '全日有給';
      const displayCount = leaveType === 2 ? (count * 0.5) + '日' : count + '日';
      return {
        key: c.staffName + '-' + startStr,
        name: c.staffName,
        dateRange,
        count,
        displayCount,
        leaveType,
        leaveTypeText,
        reason: c.reason,
        ids: c.ids,
        leaves: c.leaves,
        pendingCount: c.pendingCount,
        allApproved,
        statusText,
      };
    },
    changeYear(dir) {
      this.currentDate = new Date(this.currentDate.getFullYear() + dir, this.currentDate.getMonth(), 1);
      this.loadLeaves();
    },
    selectMonth(m) {
      this.currentDate = new Date(this.currentDate.getFullYear(), m, 1);
      this.loadLeaves();
    },
    setViewMode(mode) {
      this.viewMode = mode;
      try { sessionStorage.setItem('paidLeavePage.viewMode', mode); } catch (e) { /* ignore */ }
      this.loadLeaves();
    },
    toggleStaffExpanded(staffId) {
      this.expandedStaff = {
        ...this.expandedStaff,
        [staffId]: !this.expandedStaff[staffId],
      };
    },
    expandAll() {
      const map = {};
      this.staffSummaries.forEach(s => { map[s.staffId] = true; });
      this.expandedStaff = map;
    },
    collapseAll() {
      this.expandedStaff = {};
    },
    loadLeaves() {
      const shop_id = this.$store.getters.shop;
      let url = process.env.VUE_APP_API_PATH + '/shop/paidLeaveList/' + shop_id;
      // 月別ビューのみ年月で絞り込む。人別ビューは全期間取得
      if (this.viewMode === 'month') {
        const year = this.currentDate.getFullYear();
        const month = this.currentDate.getMonth() + 1;
        url += '?year=' + year + '&month=' + month;
      }
      this.$http.get(url)
      .then(res => { this.leaves = res.data.data || []; })
      .catch(e => console.log(e));
    },
    statusClass(status) {
      if (status === '承認済み') return 'badge-status badge-approved';
      if (status === '承認待ち') return 'badge-status badge-pending';
      return 'badge-status badge-revision';
    },
    async bulkApprove(ids, status) {
      const action = status === 2 ? '承認' : '却下';
      if (!confirm(ids.length + '件の有給申請を' + action + 'しますか？')) return;
      for (const id of ids) {
        try { await this.$http.put(process.env.VUE_APP_API_PATH + '/shop/paidLeave/' + id, { approval_status: status }); } catch (e) { console.log(e); }
      }
      this.$toast.success(ids.length + '件を' + action + 'しました');
      this.loadLeaves();
    },
    async bulkDelete(ids) {
      if (!confirm(ids.length + '件の有給申請を削除しますか？')) return;
      for (const id of ids) {
        try { await this.$http.delete(process.env.VUE_APP_API_PATH + '/shop/paidLeave/' + id); } catch (e) { console.log(e); }
      }
      this.$toast.success('削除しました');
      this.loadLeaves();
    },
    exportGroupPdf(group) {
      const win = window.open('', '_blank');
      let rows = '';
      group.leaves.forEach((l, i) => {
        rows += '<tr><td>' + (i + 1) + '</td><td>' + l.date + '</td></tr>';
      });
      win.document.write(`
        <html><head><title>有給休暇申請書</title>
        <style>
          body { font-family: 'Noto Sans JP', 'Hiragino Sans', sans-serif; padding: 40px; max-width: 700px; margin: 0 auto; }
          h2 { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #333; padding-bottom: 10px; }
          .info-row { display: flex; margin-bottom: 8px; }
          .info-label { width: 120px; font-weight: bold; }
          table { width: 100%; border-collapse: collapse; margin: 20px 0; }
          th, td { border: 1px solid #999; padding: 8px 12px; text-align: left; font-size: 14px; }
          th { background: #f0f0f0; }
          .footer { margin-top: 40px; display: flex; justify-content: space-between; }
          .stamp-box { width: 100px; height: 100px; border: 1px solid #999; text-align: center; line-height: 100px; font-size: 12px; color: #999; }
          .stamp-label { text-align: center; font-size: 12px; margin-top: 4px; }
        </style></head><body>
        <h2>有給休暇申請書</h2>
        <div class="info-row"><span class="info-label">申請者</span><span>${group.name}</span></div>
        <div class="info-row"><span class="info-label">種別</span><span>${group.leaveTypeText}</span></div>
        <div class="info-row"><span class="info-label">申請期間</span><span>${group.dateRange}</span></div>
        <div class="info-row"><span class="info-label">日数</span><span>${group.displayCount}</span></div>
        <div class="info-row"><span class="info-label">申請理由</span><span>${group.reason || '-'}</span></div>
        <table><thead><tr><th>#</th><th>日付</th></tr></thead><tbody>${rows}</tbody></table>
        <div class="footer">
          <div><div class="stamp-box">承認印</div><div class="stamp-label">管理者</div></div>
          <div><div class="stamp-box">確認印</div><div class="stamp-label">担当者</div></div>
        </div>
        </body></html>
      `);
      win.document.close();
      setTimeout(() => { win.print(); }, 500);
    },
  },
};
</script>

<style scoped>
.pl-header {
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 16px; flex-wrap: wrap; gap: 12px;
}
.month-selector { }
.year-selector { display: flex; align-items: center; justify-content: center; gap: 12px; margin-bottom: 8px; }
.year-nav-btn { width: 32px; height: 32px; border-radius: 50%; border: 1.5px solid #d2d6dc; background: #fff; color: #4a5568; font-size: 12px; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: all 0.2s; }
.year-nav-btn:hover { background: #049C4C; border-color: #049C4C; color: #fff; }
.year-label { font-size: 20px; font-weight: 700; min-width: 80px; text-align: center; }
.month-btn-group { display: flex; flex-wrap: wrap; gap: 4px; }
.month-btn { padding: 6px 12px; font-size: 13px; font-weight: 600; border: 1.5px solid #d2d6dc; border-radius: 6px; background: #fff; color: #4a5568; cursor: pointer; transition: all 0.15s; min-width: 48px; }
.month-btn:hover { background: #edf2f7; border-color: #a0aec0; }
.month-btn.active { background: #049C4C; color: #fff; border-color: #049C4C; }

.pl-table { margin: 0; }
.pl-table thead th {
  font-size: 13px; font-weight: 700; color: #4a5568; background: #f7fafc;
  border-bottom: 2px solid #e2e8f0; padding: 12px 10px;
}
.pl-table tbody td {
  padding: 12px 10px; font-size: 14px; vertical-align: middle; border-color: #edf2f7;
}
.td-name { font-weight: 600; color: #1a202c; white-space: nowrap; }
.td-date { font-weight: 600; white-space: nowrap; color: #049C4C; }
.td-reason { max-width: 250px; color: #4a5568; }
.td-actions { width: 180px; }
.action-btns { display: flex; gap: 4px; }

.badge-status { font-size: 12px; font-weight: 600; padding: 4px 10px; border-radius: 4px; display: inline-block; white-space: nowrap; }
.badge-approved { background: #c6f6d5; color: #22543d; }
.badge-pending { background: #fefcbf; color: #744210; }
.badge-revision { background: #fed7d7; color: #9b2c2c; }

.btn-approve {
  background: #c6f6d5; color: #22543d; border: 1px solid #9ae6b4;
  font-size: 12px; padding: 4px 8px; border-radius: 4px;
}
.btn-approve:hover { background: #049C4C; color: #fff; }
.btn-revision {
  background: #fed7d7; color: #9b2c2c; border: 1px solid #feb2b2;
  font-size: 12px; padding: 4px 8px; border-radius: 4px;
}
.btn-revision:hover { background: #e53e3e; color: #fff; }

.badge-full { font-size: 11px; font-weight: 600; padding: 3px 8px; border-radius: 4px; background: #c6f6d5; color: #22543d; }
.badge-half { font-size: 11px; font-weight: 600; padding: 3px 8px; border-radius: 4px; background: #fefcbf; color: #744210; }

.pl-view-toggle { display: flex; gap: 4px; }
.view-btn {
  padding: 8px 16px; font-size: 13px; font-weight: 600;
  border: 1.5px solid #d2d6dc; background: #fff; color: #4a5568;
  border-radius: 6px; cursor: pointer; transition: all 0.15s;
}
.view-btn:hover { background: #edf2f7; border-color: #a0aec0; }
.view-btn.active { background: #049C4C; color: #fff; border-color: #049C4C; }

.staff-row { cursor: pointer; transition: background-color 0.15s; }
.staff-row:hover { background-color: #f0faf4 !important; }
.staff-section { margin-bottom: 12px; border: 1px solid #e2e8f0; border-radius: 8px; overflow: hidden; }
.staff-section-clickable { cursor: pointer; user-select: none; }
.staff-section-clickable:hover { background: #edf2f7; }
.staff-actions { display: flex; gap: 8px; margin-bottom: 12px; }
.staff-section-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 12px 16px; background: #f7fafc; border-bottom: 1px solid #e2e8f0;
}
.staff-section-name { font-weight: 700; font-size: 15px; color: #1a202c; }
.staff-section-summary { font-size: 12px; color: #4a5568; }

.pl-toolbar { display: flex; gap: 12px; align-items: center; }
.pl-filter { display: flex; gap: 6px; align-items: center; }
.pl-filter-label { font-size: 13px; font-weight: 600; color: #4a5568; margin: 0; }

/* 作成モーダル */
.pl-modal-overlay {
  position: fixed; top:0; left:0; width:100%; height:100%; background: rgba(0,0,0,0.45);
  z-index: 1060; display: flex; align-items: center; justify-content: center;
}
.pl-modal-box {
  background: #fff; border-radius: 12px; width: 480px; max-width: 95vw; max-height: 90vh;
  box-shadow: 0 20px 60px rgba(0,0,0,0.25); display: flex; flex-direction: column; overflow: hidden;
}
.pl-modal-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 20px; border-bottom: 1px solid #e2e8f0;
}
.pl-modal-title { font-weight: 700; font-size: 16px; margin: 0; color: #1a202c; }
.pl-modal-close { border: none; background: none; font-size: 24px; color: #718096; cursor: pointer; padding: 0 4px; line-height: 1; }
.pl-modal-close:hover { color: #e53e3e; }
.pl-modal-body { padding: 16px 20px; overflow-y: auto; }
.pl-modal-footer { padding: 12px 20px; border-top: 1px solid #e2e8f0; display: flex; justify-content: flex-end; gap: 8px; }

.pl-field { margin-bottom: 12px; }
.pl-field label { font-size: 13px; font-weight: 600; color: #4a5568; display: block; margin-bottom: 6px; }
.required { color: #e53e3e; font-weight: 700; }
.pl-field-row { display: flex; gap: 12px; }
.pl-field-row .pl-field { flex: 1; }
.pl-radio-group { display: flex; gap: 16px; padding-top: 4px; }
.pl-radio-group label { display: flex; align-items: center; gap: 4px; font-weight: 500; cursor: pointer; }
.pl-note { font-size: 12px; color: #718096; margin: 8px 0 0 0; }
</style>
